import * as React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Box, flameTheme, Flex } from '@lightspeed/flame/Core';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import { Select } from '@lightspeed/flame/Select';
import { Text, TextLink } from '@lightspeed/flame/Text';
import clamp from 'lodash/clamp';
import orderBy from 'lodash/orderBy';

import { LabelSize, LabelTemplate, SUPPORTED_LABELS } from 'label/utils/types';
import { toBoolean } from 'pages/utils/sanitize';
import { useKeyDown } from 'root/apps/hooks';
import { getTemplatesByType } from 'root/apps/label/utils/templateHelpers';

import { useLabelPrintForm, useLocalStorage } from './hooks';
import {
  AlertText,
  StyledFlex,
  StyledInfoAlert,
  StyledLabel,
  StyledWarningAlert,
  UnstyledButton,
} from './HubLabelPrintBody.style';
import { MAX_LABELS, MIN_LABELS } from './HubLabelPrintModal';
import { InputStepper } from './InputStepper';
import LabelPreview from './LabelPreview';

const labelMarginsRange = {
  min: -20,
  max: 20,
};

export const HubLabelPrintBody: React.FC<{
  isLoading: boolean;
  isListOfLabels: boolean;
  labelPrintForm: ReturnType<typeof useLabelPrintForm>;
  submit: () => void;
  toggleContent: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}> = ({ isLoading, isListOfLabels, labelPrintForm, submit, toggleContent, closeModal }) => {
  const { colors } = flameTheme;
  const [labelTemplates, setLabelTemplates] = React.useState<LabelTemplate[] | undefined>([]);
  const [errorFetchingLabelTemplates, setErrorFetchingLabelTemplates] = React.useState(false);
  const [labelEditorTemplateSelected, setLabelEditorTemplateSelected] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(true);
  const [isConfigurationLinkVisible, setIsConfigurationLinkVisible] = useLocalStorage(
    'LABEL_CONFIGURATION_LINK',
    toBoolean(window.merchantos.session.hasRight('admin'))
  );

  const {
    handleSubmit,
    setValue,
    labelMargins,
    labelSize,
    formState,
    quantity,
    labelTemplate,
    setPreviousLabelTemplate,
    previousLabelTemplate,
    setPreviousLabelSize,
  } = labelPrintForm;

  React.useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data } = await getTemplatesByType('label');
        const alphabeticalTemplateList = orderBy(data, ['name'], ['asc']);

        setLabelTemplates(alphabeticalTemplateList);
        setLabelEditorTemplateSelected(!!labelTemplate);

        // Make sure the preview and printed template are always the one from the API, not from cache
        if (labelTemplate?.name && alphabeticalTemplateList.length) {
          const lastPrintedTemplate = alphabeticalTemplateList.find(
            ({ name }) => name === labelTemplate.name
          );
          setPreviousLabelTemplate(lastPrintedTemplate);
        }

        setIsFetching(false);
      } catch (error) {
        setErrorFetchingLabelTemplates(true);
      }
    };

    fetchTemplates();

    return () => {
      setLabelTemplates([]);
    };
  }, [labelTemplate, setPreviousLabelTemplate]);

  const { errors } = formState;
  const imageSize = labelSize || LabelSize.NORMAL;

  const editLabelMargin = (amount: number, direction: 'top' | 'left' | undefined) => () => {
    if (!direction) {
      return;
    }

    const next = {
      ...labelMargins,
    };

    if (!next[labelSize][direction]) {
      next[labelSize][direction] = amount;
    } else {
      next[labelSize][direction] = clamp(
        next[labelSize][direction] + amount,
        labelMarginsRange.min,
        labelMarginsRange.max
      );
    }

    setValue('labelMargins', next);
  };

  const editLabelQuantity = (amount: number) => () => {
    const next = {
      ...quantity,
    };
    if (next.labels === undefined) {
      next.labels = amount + 1;
    } else {
      next.labels = clamp(next.labels + amount, MIN_LABELS, MAX_LABELS);
    }
    setValue('quantity', next);
  };

  useKeyDown('Enter', handleSubmit(submit));

  const getLabelEditorTemplate = (value: LabelSize | LabelTemplate | string) =>
    labelTemplates?.find((t) => t.name === value);

  const setLabelTemplate = (
    isLabelEditorTemplate: boolean,
    labelTemplate: LabelTemplate | undefined,
    labelSize: LabelSize
  ) => {
    setLabelEditorTemplateSelected(isLabelEditorTemplate);
    setValue('labelTemplate', labelTemplate);
    setValue('labelSize', labelSize);
    setPreviousLabelSize(labelSize);
    setPreviousLabelTemplate(labelTemplate);
  };

  const handleLabelTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      target: { value },
    } = event;

    const labelEditorTemplate: LabelTemplate | undefined = getLabelEditorTemplate(
      value as LabelSize | LabelTemplate | string
    );

    if (labelEditorTemplate) {
      const labelEditorTemplateSize = JSON.parse(labelEditorTemplate.content).size.toLowerCase();
      setLabelTemplate(true, labelEditorTemplate, labelEditorTemplateSize);
      return;
    }

    setLabelTemplate(false, undefined, value as LabelSize);
  };

  const renderBaseTemplates = () =>
    SUPPORTED_LABELS.map(({ label, value, dimensions }) => (
      <option data-test={`print-label-select-size-${value}`} value={value} key={value}>
        {label} {dimensions ? ` - ${dimensions}` : null}
      </option>
    ));

  const renderLabelTemplateDropdown = () =>
    !isFetching ? (
      <Select
        data-test="print-label-select-template"
        onChange={(event) => handleLabelTemplateChange(event)}
        value={labelTemplateValue}
      >
        {labelTemplates &&
          [
            {
              name: 'Select',
              value: '',
              size: undefined,
            },
            ...labelTemplates,
          ].map(({ name }) => (
            <option
              data-test={`print-label-select-template-${name}`}
              value={name === 'Select' ? '' : name}
              key={name}
              className="notranslate"
            >
              {name}
            </option>
          ))}
        {renderBaseTemplates()}
      </Select>
    ) : (
      <Bone height={6} />
    );

  let labelTemplateValue: string | undefined;
  if (labelEditorTemplateSelected) labelTemplateValue = labelTemplate?.name;
  else labelTemplateValue = labelSize;

  return (
    <Flex mb={3}>
      <Flex flexDirection="column" width="50%" pr={4}>
        <StyledLabel>Current label</StyledLabel>
        <StyledFlex>
          {isFetching ? <Bone width="228px" height="100px" /> : null}
          {!isFetching && labelEditorTemplateSelected && previousLabelTemplate ? (
            <LabelPreview labelTemplate={previousLabelTemplate} labelSize={labelSize} />
          ) : null}
          {!isFetching && (!labelEditorTemplateSelected || !labelEditorTemplateSelected) ? (
            <img
              style={{
                alignSelf: 'center',
                maxHeight: '100%',
                maxWidth: '100%',
              }}
              data-test="print-label-image"
              className={`label-size-${imageSize}`}
              src={`https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/label/label-size-${imageSize}.svg`}
              alt={`${imageSize} labels`}
            />
          ) : null}
        </StyledFlex>
        <Flex flexDirection="column" mt={4}>
          {isConfigurationLinkVisible ? (
            <StyledInfoAlert
              type="info"
              icon={<IconInfo baseColor1={colors.secondary} />}
              onClose={() => setIsConfigurationLinkVisible(false)}
            >
              <AlertText data-test="label-editor-info-alert">
                You can add templates from{' '}
                <TextLink
                  data-test="print-label-configuration-link"
                  onClick={() => {
                    window.PageRouter.goTo('/settings/label-editor/');
                    setIsConfigurationLinkVisible(false);
                    closeModal();
                  }}
                >
                  Settings {'>'} Label Editor
                </TextLink>
              </AlertText>
            </StyledInfoAlert>
          ) : null}
        </Flex>
      </Flex>
      <Flex flexDirection="column" width="50%" pl={4}>
        {errorFetchingLabelTemplates && (
          <StyledWarningAlert type="warning" mb={3}>
            Custom labels are not available. Try refreshing your browser.
          </StyledWarningAlert>
        )}
        <Box mb={3}>
          <StyledLabel>Select label template</StyledLabel>
          {renderLabelTemplateDropdown()}
        </Box>
        <Box mb={3}>
          <InputStepper
            labelText="Select number of labels"
            descriptionText={`Make sure that the label printer is loaded with enough ${labelSize} labels`}
            isInputDisabled={isLoading || isFetching || isListOfLabels || !labelSize}
            inputRef={'quantity.labels' as const}
            isIncrementButtonDisabled={
              !labelSize || quantity.labels >= MAX_LABELS || isListOfLabels
            }
            isDecrementButtonDisabled={
              !labelSize || quantity.labels <= MIN_LABELS || isListOfLabels
            }
            minInputValue={MIN_LABELS}
            maxInputValue={MAX_LABELS}
            status={errors.quantity ? 'error' : undefined}
            stepperFunction={editLabelQuantity}
            printForm={labelPrintForm}
            placeholder="1"
          />
        </Box>
        <Flex mb={1} justifyContent="space-between">
          <Box>
            <InputStepper
              labelText="Adjust top label margins"
              isInputDisabled={isLoading || isFetching || !labelSize}
              inputRef={labelSize ? (`labelMargins.${labelSize}` as const) : 'default'}
              labelMargin="top"
              isIncrementButtonDisabled={
                !labelSize || labelMargins[labelSize].top >= labelMarginsRange.max
              }
              isDecrementButtonDisabled={
                !labelSize || labelMargins[labelSize].top <= labelMarginsRange.min
              }
              status={errors?.labelMargins?.[labelSize]?.top ? 'error' : undefined}
              stepperFunction={editLabelMargin}
              printForm={labelPrintForm}
              maxInputValue={labelMarginsRange.max}
              minInputValue={labelMarginsRange.min}
            />
          </Box>
          <Box>
            <InputStepper
              labelText="Adjust left label margins"
              isInputDisabled={isLoading || isFetching || !labelSize}
              inputRef={labelSize ? (`labelMargins.${labelSize}` as const) : 'default'}
              labelMargin="left"
              isIncrementButtonDisabled={
                !labelSize || labelMargins[labelSize].left >= labelMarginsRange.max
              }
              isDecrementButtonDisabled={
                !labelSize || labelMargins[labelSize].left <= labelMarginsRange.min
              }
              status={errors?.labelMargins?.[labelSize]?.left ? 'error' : undefined}
              stepperFunction={editLabelMargin}
              printForm={labelPrintForm}
              maxInputValue={labelMarginsRange.max}
              minInputValue={labelMarginsRange.min}
            />
          </Box>
        </Flex>
        <Text>Label margins can be between -20 to 20</Text>
        <Flex mt={4}>
          <UnstyledButton
            data-test="advanced-settings-button"
            onClick={() => toggleContent(true)}
            disabled={labelEditorTemplateSelected}
          >
            Advanced settings
          </UnstyledButton>
        </Flex>
      </Flex>
    </Flex>
  );
};
